import { CanMatchFn } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { inject } from '@angular/core';
import { Observable, from, map } from 'rxjs';
import { msalSettled } from './msal-settle';

/**
 * Is there an unconsumed auth response sitting in the URL right now?
 *
 * MSAL returns from Entra with the response in the FRAGMENT (response_mode=
 * fragment, confirmed from the live authorize request), landing on the app's
 * redirectUri — which is the site root. Anything that navigates before
 * MsalRedirectComponent has read that fragment destroys it: Angular builds a new
 * UrlTree for the redirect and the hash does not survive.
 *
 * Kept as a pure function on the raw hash so it can be reasoned about and tested
 * without a browser, a router or MSAL.
 */
export function hasPendingRedirectResponse(hash: string | null | undefined): boolean {
  const h = (hash ?? '').replace(/^#/, '');
  if (!h) return false;
  // `code` and `error` are the two answers Entra gives. `state` alone is not
  // enough — it appears in other fragments — and requiring it would miss a
  // malformed response we still must not stomp on.
  return /(^|&)(code|error)=/.test(h);
}

/**
 * Hold a navigation until MSAL has consumed a redirect response.
 *
 * THE BUG THIS EXISTS FOR, measured on the live dev server: returning to '/'
 * with `#code=…`, the router's landing rule ('' -> projects) and the
 * unauthenticated path to /login fired FIRST. The fragment was gone before MSAL
 * looked, so handleRedirectPromise found nothing — no error, no rejection,
 * silence — and localStorage held `msal.version` and nothing else. A synthetic
 * `#code=…` reproduced it exactly: hash "" and not one line of MSAL output.
 *
 * This is the SAME CLASS as the reload storm and the project-restore race: a
 * navigation deciding before MSAL has settled. That is three separate places
 * now, which is why the wait belongs behind a shared helper rather than being
 * re-derived each time.
 *
 * canMatch, not canActivate: a route with `redirectTo` never runs canActivate,
 * and the landing redirect is precisely the navigation that has to be held.
 *
 * Costs nothing in the normal case — with no auth response in the URL it does
 * not wait at all, and `inProgress$` replays its current value when it does.
 */
export const msalRedirectSettledGuard: CanMatchFn = (): Observable<boolean> | boolean => {
  let hash = '';
  try {
    hash = globalThis.location?.hash ?? '';
  } catch {
    hash = '';
  }
  if (!hasPendingRedirectResponse(hash)) {
    return true;
  }
  const broadcast = inject(MsalBroadcastService);
  // Always resolves true: this delays the navigation, it never denies it.
  // Denying would strand the user on a blank route; the only job here is to let
  // MSAL go first.
  return from(msalSettled(broadcast, 'msalRedirectSettledGuard')).pipe(map(() => true));
};
